import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { ISplitOptions } from "../../models/text/ISplitOptions";
import { StringObject } from "../../objects/StringObject";
import { BuildDOMTree } from "../../utils/text/BuildDOMTree";
import { BuildTokens } from "../../utils/text/BuildTokens";
import { FontMetrics } from "../../utils/text/CanvasKerningApplier";
import { LayoutMeasurer } from "../../utils/text/LayoutMeasurer";
import { LayoutLine, SplitMeasuredTokens } from "../../utils/text/SplitMeasuredTokens";
import { FitFontSizeCalculator } from "../../utils/text/FitFontSizeCalculator";
import { FitFontSizeRenderer } from "../../utils/text/FitFontSizeRenderer";
import { SplitAlignValueCalculator } from "../../utils/text/SplitAlignValueCalculator";
import { SplitOptionsPredicates } from "../../utils/text/SplitOptionsPredicates";

/**
 * StringSplit module: splits text into lines, words, chars,
 * computes alignment/random values, and applies CSS vars.
 */
export class StringSplit extends StringModule {
  /** Last content-width (px) at which each element was split. Keyed by element itself. */
  private lastSplitWidth = new WeakMap<HTMLElement, number>();
  private tamperObservers = new WeakMap<HTMLElement, MutationObserver>();

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "split";
    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    //this.permissions.mobile.rebuild.scrollHeight = false;
  }

  onInit(): void {
    const rebuild = () => {
      this.objectsOnPage.forEach((object) => {
        const element = object.htmlElement;
        if (!element) return;

        const options = this.getSplitOptions(element);
        if (SplitOptionsPredicates.needsForcedRebuildOnFontLoad(options)) {
          this.lastSplitWidth.delete(element);
        }

        this.onObjectConnected(object);
      });
    };

    document.fonts.ready.then(rebuild);
    document.fonts.addEventListener("loadingdone", rebuild);
  }

  override onObjectDisconnected(object: StringObject): void {
    const element = object.htmlElement;
    if (!element) return;
    this.lastSplitWidth.delete(element);
    this.tamperObservers.get(element)?.disconnect();
    this.tamperObservers.delete(element);
  }

  onResizeWidth(): void {
    this.objectsOnPage.forEach((object) => {
      const element = object.htmlElement;
      if (!element) return;

      const options = this.getSplitOptions(element);
      if (!SplitOptionsPredicates.needsWidthRebuild(options)) {
        return;
      }

      this.onObjectConnected(object);
    });
  }

  onObjectConnected(object: StringObject): void {
    const element = object.htmlElement;
    if (!element) return;

    const isAlreadySplit = element.classList.contains("-splitted");
    // Skip re-split when called from onResizeWidth and the element's content width
    // hasn't changed — prevents feedback loops caused by animations or other
    // unrelated ResizeObserver triggers on the scroll container. Measured without
    // touching classList: splitting doesn't rewrap the container's own box (only
    // its children), so -splitted has no effect on this measurement, and toggling
    // it here on every no-op call caused a forced-layout class flicker under
    // frequent external resize noise.
    const cs = window.getComputedStyle(element);
    const currentContentWidth = this.getElementContentWidth(element, cs);
    const lastWidth = this.lastSplitWidth.get(element);
    if (
      isAlreadySplit &&
      lastWidth !== undefined &&
      Math.abs(currentContentWidth - lastWidth) < 1
    ) {
      return;
    }
    this.lastSplitWidth.set(element, currentContentWidth);

    this.performSplit(object);
  }

  private performSplit(object: StringObject): void {
    const element = object.htmlElement;
    if (!element) return;

    const isAlreadySplit = element.classList.contains("-splitted");
    let originalHtml = element.getAttribute("string-split-original-html");
    let originalText = element.getAttribute("string-split-original");

    if (originalHtml === null && originalText !== null && isAlreadySplit) {
      originalHtml = originalText;
      originalText = this.extractTextContent(originalHtml);
      element.setAttribute("string-split-original-html", originalHtml);
      element.setAttribute("string-split-original", originalText);
    }

    if (!isAlreadySplit || originalHtml === null || originalText === null) {
      originalHtml = this.escapeAttribute(element.innerHTML);
      originalText = element.textContent ?? "";
      element.setAttribute("string-split-original-html", originalHtml);
      element.setAttribute("string-split-original", originalText);
    }

    if (isAlreadySplit) {
      element.classList.remove("-splitted");
    }

    this.tamperObservers.get(element)?.disconnect();

    try {
      object.htmlElement.innerHTML = originalHtml;
      const attr =
        element.getAttribute("string-split") ?? element.getAttribute("data-string-split") ?? "";
      const options: ISplitOptions = this.tools.optionsParser.process({
        attributeValue: attr,
      });
      const { fragment, result, extraProps } = this.split(element, options);
      object.setProperty("nodes", fragment.childNodes);
      element.setAttribute("aria-label", originalText);
      element.innerHTML = "";
      element.appendChild(result);
      this.applyFlexLineBreaks(element, options);
      element.classList.add("-splitted");

      extraProps.forEach((value: string, key: string) => {
        element.style.setProperty(key, value);
      });

      const restoreAfter = element.getAttribute("string-split-restore-after");
      if (restoreAfter && !isNaN(Number(restoreAfter))) {
        setTimeout(() => {
          element.innerHTML = originalHtml!;
          element.classList.add("-restored");
        }, Number(restoreAfter));
      }

      this.watchForTampering(object);
      this.events.emit(this.getObjectEventName(object, "split:rebuilt"), element);
      element.dispatchEvent(new CustomEvent("string:split:rebuilt", { bubbles: true }));
    } finally {
      if (!element.classList.contains("-splitted")) {
        element.classList.add("-splitted");
      }
    }
  }

  private watchForTampering(object: StringObject): void {
    const element = object.htmlElement;
    if (!element) return;

    let observer = this.tamperObservers.get(element);
    if (!observer) {
      observer = new MutationObserver(() => {
        if (!element.isConnected) return;
        this.performSplit(object);
      });
      this.tamperObservers.set(element, observer);
    }
    observer.observe(element, { childList: true });
  }

  private extractTextContent(html: string): string {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent ?? "";
  }

  private getSplitOptions(element: HTMLElement): ISplitOptions {
    const attr =
      element.getAttribute("string-split") ?? element.getAttribute("data-string-split") ?? "";
    return this.tools.optionsParser.process({
      attributeValue: attr,
    });
  }

  private applyFlexLineBreaks(element: HTMLElement, options: ISplitOptions): void {
    const display = window.getComputedStyle(element).display;
    if (display !== "flex" && display !== "inline-flex") return;

    const breaks = Array.from(element.querySelectorAll("br"));
    for (const br of breaks) {
      const marker = this.createFlexLineBreakMarker();
      const line = br.closest(".-s-line");
      if (line && line.parentElement === element) {
        br.remove();
        line.after(marker);
      } else {
        br.replaceWith(marker);
      }
    }
  }

  private createFlexLineBreakMarker(): HTMLElement {
    const marker = document.createElement("span");
    marker.setAttribute("aria-hidden", "true");
    marker.classList.add("-s-line-break");
    // marker.style.flexBasis = "100%";
    // marker.style.width = "0";
    // marker.style.height = "0";
    // marker.style.overflow = "hidden";
    // marker.style.pointerEvents = "none";
    return marker;
  }

  private getBlockContainerContentWidth(element: HTMLElement): number {
    let el: HTMLElement | null = element.parentElement;
    while (el) {
      const cs = window.getComputedStyle(el);
      const d = cs.display;
      const isInline = d === "inline" || d === "inline-block" || d === "ruby";
      if (!isInline) {
        const layoutWidth = el.getBoundingClientRect().width || el.clientWidth;
        return Math.max(
          0,
          layoutWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0),
        );
      }
      el = el.parentElement;
    }
    return 0;
  }

  private getElementContentWidth(
    element: HTMLElement,
    cs = window.getComputedStyle(element),
  ): number {
    const padding = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const layoutWidth = element.clientWidth || element.getBoundingClientRect().width;
    const parentContentWidth = this.getBlockContainerContentWidth(element);
    const shouldPreferParentWidth =
      parentContentWidth > layoutWidth + 1 &&
      !element.style.width &&
      (cs.display === "inline" || cs.display === "inline-flex" || cs.display === "inline-grid");

    if (shouldPreferParentWidth) {
      return Math.max(0, parentContentWidth - padding);
    }

    if (layoutWidth > 0) {
      return Math.max(0, layoutWidth - padding);
    }

    return Math.max(0, parentContentWidth - padding);
  }

  public split(element: HTMLElement, options: ISplitOptions) {
    const fontMetrics = new FontMetrics(element);
    const frag = document.createDocumentFragment();
    element.childNodes.forEach((ch) => frag.appendChild(ch.cloneNode(true)));
    const tokens = BuildTokens(element.childNodes as NodeListOf<ChildNode>);
    const measured = LayoutMeasurer(tokens, element, fontMetrics, options);
    const layoutLines: LayoutLine[] = SplitMeasuredTokens(measured, element, fontMetrics, options);

    const fitContext = options.fit
      ? FitFontSizeRenderer.getFitContext(
          layoutLines,
          element,
          this.getElementContentWidth(element),
        )
      : null;
    const fitExtraProps = fitContext
      ? FitFontSizeCalculator.applyToLines(layoutLines, options, fitContext)
      : new Map<string, string>();

    SplitAlignValueCalculator.applyToLines(layoutLines, options);

    const dom = BuildDOMTree(layoutLines, options, fontMetrics);
    fitExtraProps.forEach((v, k) => dom.extraProps.set(k, v));

    if (fitContext) {
      FitFontSizeRenderer.refineFitFontSize(
        element,
        dom.fragment,
        dom.extraProps,
        layoutLines,
        options,
        fitContext,
      );
    }

    return {
      fragment: frag,
      result: dom.fragment,
      extraProps: dom.extraProps,
    };
  }

  /**
   * Escapes the `src` attribute in a given string by removing the quotes around the URL.
   *
   * This method searches for occurrences of `src="URL"` where `URL` is an HTTP or HTTPS link,
   * and replaces it with `src=URL` (removing the quotes around the URL).
   *
   * @param str - The input string containing the `src` attributes to be escaped.
   * @returns The modified string with escaped `src` attributes.
   */
  private escapeAttribute(str: string): string {
    return str.replace(/src="(https?:\/\/[^"\s]+)"/g, "src=$1");
  }
}
